import { createContext, Suspense, useContext, useMemo, useRef, type ReactNode } from 'react';
import { can } from '@blackblack/shared';
import { compile, ParamsProvider, useLocation } from '../lib/router';
import { useAuth } from '../state/auth';
import { Button, EmptyState, Spinner } from '../components/ui/primitives';
import { ROUTES, type RouteDef } from './routes';
import { navigate } from '../lib/router';
import styles from './shell.module.css';

const matchers = ROUTES.map((r) => ({ route: r, match: compile(r.path) }));

export function matchLocation(pathname: string): { route: RouteDef; params: Record<string, string> } | null {
  for (const m of matchers) {
    const params = m.match(pathname);
    if (params) return { route: m.route, params };
  }
  return null;
}

const PageActiveContext = createContext(true);
/** False while a kept-alive page is hidden (e.g. pause the feed video). */
export const usePageActive = () => useContext(PageActiveContext);

/**
 * Renders the active route. Tab roots (keepAlive) stay mounted but hidden so
 * the feed position, scroll offsets and loaded data survive tab switches.
 */
export function Outlet() {
  const loc = useLocation();
  const current = matchLocation(loc.pathname);
  const alive = useRef(new Map<string, { route: RouteDef; params: Record<string, string> }>());
  if (current?.route.keepAlive) alive.current.set(current.route.path, current);

  const keptAlive = [...alive.current.values()];
  const activeKey = current?.route.keepAlive ? current.route.path : null;

  return (
    <>
      {keptAlive.map((m) => {
        const active = m.route.path === activeKey;
        return (
          <PageActiveContext.Provider key={m.route.path} value={active}>
            <div className={styles.pageSlot} hidden={!active}>
              <RouteView route={m.route} params={m.params} />
            </div>
          </PageActiveContext.Provider>
        );
      })}
      {!activeKey && (
        <div className={styles.pageSlot} key={loc.key + loc.pathname}>
          {current ? <RouteView route={current.route} params={current.params} /> : <NotFound />}
        </div>
      )}
    </>
  );
}

function RouteView({ route, params }: { route: RouteDef; params: Record<string, string> }) {
  const { status, me, requireAuth } = useAuth();
  const Component = route.component;
  const content = useMemo<ReactNode>(() => <Component />, [Component]);

  if ((route.auth || route.permission) && status === 'loading') return <CenteredSpinner />;
  if ((route.auth || route.permission) && status === 'anon') {
    return (
      <EmptyState
        icon="lock"
        title="Log in om verder te gaan"
        text="Maak een account of log in om dit te gebruiken."
        action={<Button onClick={() => requireAuth('Log in om verder te gaan')}>Inloggen</Button>}
      />
    );
  }
  if (route.permission && me && !can(me.role, route.permission)) {
    return <EmptyState icon="shield" title="Geen toegang" text="Je hebt geen rechten voor deze pagina." action={<Button variant="secondary" onClick={() => navigate('/')}>Naar home</Button>} />;
  }
  return (
    <ParamsProvider params={params}>
      <Suspense fallback={<CenteredSpinner />}>{content}</Suspense>
    </ParamsProvider>
  );
}

export function CenteredSpinner() {
  return (
    <div className={styles.centered}>
      <Spinner />
    </div>
  );
}

function NotFound() {
  return <EmptyState icon="alert" title="Pagina niet gevonden" text="Deze link werkt niet (meer)." action={<Button onClick={() => navigate('/')}>Naar home</Button>} />;
}
